import { useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { setRecoil } from "recoil-nexus";
import ApiController from "../../../controller/ApiController";
import LogController from "../../../controller/LogController";
import {
  CONTENT_SECTOR_TYPE,
  CONTENT_STORE_TYPE,
  ContentCardDataType,
  ContentCardType,
  ContentSectorDataType,
  ContentStoreDataType,
  ContentType,
  MainSectionType,
  OwnerSectionType,
  WebStoreSelectorType,
} from "../../../controller/MasterController";
import { authState, masterStoreState } from "../../../recoil/auth";
import { langState } from "../../../recoil/lang";
import { LoadingState } from "../../../recoil/loading";
import {
  visibleCardState,
  visibleConciergeState,
  visibleCouponListState,
} from "../../../recoil/modal";
import { settingsState } from "../../../recoil/settings";
import { getDateTheme, ThemeState } from "../../../recoil/theme";
import { UseMainQueryProps } from "./type";

let modalinactiveTimer: any;
let updateInterval: any;

const modalRestTime = 60 * 1000;

export const useMain = (queries: UseMainQueryProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const currentDate = new Date();

  const auth = useRecoilValue(authState);
  const master = useRecoilValue(masterStoreState);
  // 설정 키오스크 모드
  const kioskMode = useRecoilValue(settingsState).view.kioskmode.value === "1";
  // 초기 로딩 상태
  const [loading, setLoading] = useRecoilState(LoadingState);
  // 테마
  const theme = useRecoilValue(ThemeState);
  // 언어
  const lang = useRecoilValue(langState);

  const [selectedCategory, setSelectedCategory] =
    useState<ContentSectorDataType | null>(null);
  const [selectedCard, setSelectedCard] = useState<ContentCardDataType | null>(
    null
  );

  // 주변 매장 정보
  const [voucherContent, setVoucherContent] = useState<boolean>(false);

  const modalInactiveTimeRef = useRef(0);

  // getStoreStatus 비교 값
  let list: WebStoreSelectorType | null = null;

  // 모달 자동 닫기 타이머 관련
  const modalResetTimer = () => {
    modalInactiveTimeRef.current = 0;
    clearTimeout(modalinactiveTimer);
    modalinactiveTimer = null;
    modalStartTimer();
  };
  const modalStartTimer = useCallback(() => {
    clearTimeout(modalinactiveTimer);
    modalinactiveTimer = setTimeout(() => {
      modalInactiveTimeRef.current += modalRestTime;

      if (modalInactiveTimeRef.current >= modalRestTime) {
        setRecoil(visibleCardState, false);
        setRecoil(visibleConciergeState, false);
        setRecoil(visibleCouponListState, false);

        modalInactiveTimeRef.current = 0;
        modalStartTimer();
      }
    }, modalRestTime);
  }, [modalRestTime]);

  // navigate 로직
  const navigateStore = (
    storeList: ContentStoreDataType | ContentStoreDataType[],
    // | ContentCategoryDataType,
    index: number
  ) => {
    console.log("navigate store", storeList, index);

    if (
      "store_id" in storeList
        ? storeList.isStoreAlwaysSellingCheckoutProduct
        : storeList[index].isStoreAlwaysSellingCheckoutProduct
    ) {
      navigate(`/checkout`, { state: { storeList, storeIndex: index } });
    } else {
      navigate(`/store`, { state: { storeList, storeIndex: index } });
    }
  };

  // 섹터 이동
  const navigateSector = async (item: ContentType) => {
    await getAllStoreData(item);
    if (item.content_type === CONTENT_SECTOR_TYPE) {
      setSelectedCategory(item.data as ContentSectorDataType);
      LogController.click({
        path: "StoreSelector(MainPage)",
        params: "sector",
        params2: item.data.parent_store_sector_name,
        index: item.data.parent_store_sector_id,
      });
    } else {
      navigateStore(item.data as ContentStoreDataType, -1);
    }
  };

  // sector 매장 이동
  const findSectorItem = (storeId: number) => {
    if (
      queries.storeList &&
      ((queries.storeList.main_section &&
        queries.storeList.main_section.length > 0) ||
        (queries.storeList.owner_section &&
          queries.storeList.owner_section.length > 0))
    ) {
      let findStore: any = {
        store_list: undefined,
        index: -1,
      };
      queries.storeList.owner_section.map((sector: OwnerSectionType) => {
        sector.content.map((item: ContentType, index: number) => {
          if (item.content_type === CONTENT_SECTOR_TYPE) {
            const storeIndex = item.data.store_list.findIndex(
              (data: ContentStoreDataType) => data.store_id === storeId
            );
            if (storeIndex !== -1) {
              findStore = {
                content_type: CONTENT_SECTOR_TYPE,
                data: {
                  store_list: item.data.store_list,
                  index: storeIndex,
                },
              };
            }
          } else if (item.content_type === CONTENT_STORE_TYPE) {
            if (item.data.store_id === storeId) {
              findStore = {
                content_type: CONTENT_STORE_TYPE,
                data: item.data,
              };
            }
          }
        });
      });
      queries.storeList.main_section.map((sector: MainSectionType) => {
        sector.content.map((item: ContentType, index: number) => {
          if (item.content_type === CONTENT_SECTOR_TYPE) {
            const storeIndex = item.data.store_list.findIndex(
              (data: ContentStoreDataType) => data.store_id === storeId
            );
            if (storeIndex !== -1) {
              findStore = {
                content_type: CONTENT_SECTOR_TYPE,
                data: {
                  store_list: item.data.store_list,
                  index: storeIndex,
                },
              };
            }
          } else if (item.content_type === CONTENT_STORE_TYPE) {
            if (item.data.store_id === storeId) {
              findStore = {
                content_type: CONTENT_STORE_TYPE,
                data: item.data,
              };
            }
          }
        });
      });
      return findStore;
    }
  };

  // 매장 혹은 매장과 같은 sector 데이터 모두 호출 (React-query prefetch로 미리 데이터 불러오기)
  const getAllStoreData = async (content: ContentType) => {
    if (queries.storeList && master) {
      if (content.content_type === "SECTOR") {
        content.data.store_list.map((store) => {
          queryClient.prefetchQuery({
            queryKey: ["details", store.store_id],
            queryFn: async () => {
              const res = await ApiController.store.getKnockStoreData({
                store_id: store.store_id,
                parent_store_id: master.store_id,
                user_token_id: auth.user_token_id,
                user_id: auth.user_id,
                isStoreAlwaysSellingCheckoutProduct:
                  store.isStoreAlwaysSellingCheckoutProduct,
              });
              if (res && res.data && res.data.data) {
                return res.data.data;
              } else throw new Error("details error");
            },
          });
          console.log(
            "queryData",
            store.store_id,
            queryClient.getQueryData(["details", store.store_id]),
            queryClient.getQueryCache()
          );
        });
      } else if (content.content_type === "STORE") {
        // 진입할 스토어 데이터 미리 로드
        queryClient.prefetchQuery({
          queryKey: ["details", content.data.store_id],
          queryFn: async () => {
            const res = await ApiController.store.getKnockStoreData({
              store_id: content.data.store_id,
              parent_store_id: master.store_id,
              user_token_id: auth.user_token_id,
              user_id: auth.user_id,
              isStoreAlwaysSellingCheckoutProduct:
                content.data.isStoreAlwaysSellingCheckoutProduct,
            });
            if (res && res.data && res.data.data) {
              return res.data.data;
            } else throw new Error("details error");
          },
        });
        console.log(
          "queryData",
          queryClient.getQueryData(["details", content.data.store_id])
        );
      }
    }
  };

  // 광고 매장 이동
  const navigateAdStore = (storeId: number) => {
    const findSector = findSectorItem(storeId);
    if (findSector === undefined || findSector.index === -1) {
      return;
    }
    getAllStoreData(findSector);
    if (findSector?.content_type === CONTENT_SECTOR_TYPE) {
      navigateStore(findSector.data.store_list, findSector.data.index);
    } else {
      navigateStore(findSector.data, -1);
    }
  };

  // getKnockMain 비교 호출
  const getStoreStatus = async () => {
    try {
      const response = await ApiController.store.getStoreStatus(auth.user_id);

      if (response.ok) {
        const newList: WebStoreSelectorType = response.data;

        if (JSON.stringify(list) !== JSON.stringify(newList)) {
          try {
            const knockMainData = await queryClient.fetchQuery({
              queryKey: ["store-list"],
              queryFn: () =>
                ApiController.store
                  .getKnockMain(auth.user_id, auth.user_token_id)
                  .then((res) => res.data),
            });

            if (knockMainData) {
              list = newList;
            }
          } catch (error) {
            console.error("getKnockMain 실행 실패", error);
          }
        }
      }
    } catch (err) {
      console.log("getStoreStatus err-->", err);
    }
  };

  // 모달오픈
  const openCard = (item: ContentCardType) => {
    setSelectedCard(item.data);
    setRecoil(visibleCardState, true);
    setVoucherContent(false);
    LogController.click({
      path: "StoreSelector(MainPage)",
      params: "card",
      params2: `${item.data.title} 카드 오픈`,
      index: item.data.card_id,
    });
  };
  const openVoucher = (item: ContentCardType) => {
    setSelectedCard(item.data);
    setVoucherContent(true);
    LogController.click({
      path: "StoreSelector(MainPage)",
      params: "voucher",
      params2: `${item.data.title} 바우처 맛집 오픈`,
      index: item.data.card_id,
    });
  };
  const openConciergeModal = () => {
    if (queries.concierge) {
      setRecoil(visibleConciergeState, true);
      LogController.click({
        path: "StoreSelector(MainPage)",
        params: "concierge",
        params2: `컨시어지 오픈`,
      });
    }
  };

  useEffect(() => {
    try {
      LogController.navigate({
        path: "StoreSelector(MainPage)",
        params: "navigate",
        params2: "메인 페이지",
      });
    } catch (err: any) {
      console.log(err);
    }

    modalResetTimer();

    // 5분마다 테마, getStoreStatus로 데이터 갱신
    updateInterval = setInterval(() => {
      getStoreStatus();
      if (!moment(currentDate, "MM-DD").isSame((new Date(), "MM-DD"))) {
        setRecoil(ThemeState, getDateTheme());
      }
    }, 1000 * 60 * 5);

    return () => {
      clearTimeout(modalinactiveTimer);
      modalinactiveTimer = null;
      clearInterval(updateInterval);
      updateInterval = null;
    };
  }, []);

  // 데이터 로딩 후 로딩 해제
  useEffect(() => {
    if (queries.storeList) {
      console.log("init");
      setRecoil(masterStoreState, queries.storeList.auth_section);

      if (queries.useInterphone !== undefined) {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    }
  }, [queries.storeList, queries.useInterphone]);

  return {
    currentDate,
    kioskMode,
    loading,
    theme,
    lang,
    voucherContent,
    master,
    selectedCategory,
    selectedCard,
    setVoucherContent,
    navigateStore,
    navigateSector,
    navigateAdStore,
    openCard,
    openVoucher,
    openConciergeModal,
    modalResetTimer,
  };
};
